import Alert from '@mui/material/Alert';
import DialogContent from '@mui/material/DialogContent';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { PackageListItem } from '@openx/types/package';
import { type JSX, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { PackageRowActionType } from '../../types';
import { usePackageRowActionTranslate } from '../../utils/hooks/usePackageRowActionTranslate';

const StyledContentTypography = styled(Typography)`
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`;

const StyledProceedMsgTypography = styled(Typography)`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

export type PackageStatusDialogContentProps = {
  packages: PackageListItem[];
  action: PackageRowActionType;
  isBulkMode: boolean;
  allPackages: number;
};

export const PackagesStatusDialogContent = ({
  packages,
  allPackages,
  action,
  isBulkMode,
}: PackageStatusDialogContentProps): JSX.Element => {
  const { t } = useTranslation();

  const packageRowTranslate = usePackageRowActionTranslate();
  const actionText = packageRowTranslate[action].toLowerCase();
  const packageLabel = isBulkMode ? t('packages') : t('package');

  const getWarningText = useCallback(() => {
    if (action === PackageRowActionType.DELETE) {
      return t(
        "{skiped} of the {all} selected Package(s) will not be deleted because you don't have permission to delete curated packages.",
        {
          all: allPackages,
          skiped: allPackages - packages.length,
        }
      );
    }
  }, [action, allPackages, packages.length, t]);

  return (
    <DialogContent dividers>
      {packages.length !== allPackages && (
        <Alert sx={{ marginBottom: 1.5 }} severity="error" data-test="alert-msg">
          {getWarningText()}
        </Alert>
      )}
      {!!packages.length && (
        <Typography variant="body1">
          {t('You’re about to {actionText} the following {package}:', {
            actionText,
            package: packageLabel,
          })}
        </Typography>
      )}
      {packages.map(pckg => (
        <StyledContentTypography variant="body1" key={pckg.id}>
          {pckg.name}
        </StyledContentTypography>
      ))}
      {!!packages.length && (
        <StyledProceedMsgTypography variant="body1">{t('Do you want to proceed?')}</StyledProceedMsgTypography>
      )}
    </DialogContent>
  );
};
